import { Component, Input, OnInit, ViewChild } from "@angular/core";
import { MatDialog } from "@angular/material/dialog";
import { MatPaginator } from "@angular/material/paginator";
import { MatTableDataSource } from "@angular/material/table";
import { ActivatedRoute, Router } from "@angular/router";
import { NotificationsService } from "angular2-notifications";
import { TicketStatuses } from "app/enums/TicketStatuses.enum";
import { ITicket } from "app/interfaces/i-ticket";
import { DialogConfirm } from "app/main/dialog-confirm/dialog-confirm.component";
import { environment } from "environments/environment";
import { assign, orderBy } from "lodash";
import moment from "moment-timezone";
import { NgxSpinnerService } from "ngx-spinner";
import { filter, mergeMap } from "rxjs/operators";
import { ApiTicketService } from "../../../services/api/api-ticket.service";
import { DetailComponent } from "./detail/detail.component";
import { FormControl, FormGroup } from "@angular/forms";
import {
    ITicketScheduled,
    TicketScheduleRequest,
} from "app/interfaces/i-ticket-scheduled";
import { TicketScheduledStatuses } from "app/enums/TicketScheduledStatuses.enum";
import { IFilterStatus } from "app/interfaces/i-filter-status";
import { omit } from "lodash";

@Component({
    standalone: false,
    selector: "app-appointments",
    templateUrl: "./appointments.component.html",
    styleUrls: ["./appointments.component.scss"],
})
export class AppointmentsComponent implements OnInit {
    @Input() dataSource = new MatTableDataSource();

    public range: FormGroup;
    public displayedColumns: string[] = [
        "id",
        "status",
        "date",
        "hour",
        "category",
        "operator",
        "participates",
        "delete",
    ];
    public nElemForPage: number[] = [5, 10, 15];
    public statusesTicket = TicketStatuses;
    public scheduledStatusesTicket = TicketScheduledStatuses;
    public size: number = 5;
    public pageNumber: number = 0;
    public length: number;
    public spinnerOn: boolean = false;
    public id_user: number;
    public filterStatusMap: { [key: string]: IFilterStatus } = {
        [this.statusesTicket.SCHEDULED_UNASSIGNED]: {
            label: this.scheduledStatusesTicket.NON_ASSEGNATO,
            status: this.statusesTicket.SCHEDULED_UNASSIGNED,
            color: "#e37207",
            filterable: true,
        },
        [this.statusesTicket.SCHEDULED_ASSIGNED]: {
            label: this.scheduledStatusesTicket.ASSEGNATO,
            status: this.statusesTicket.SCHEDULED_ASSIGNED,
            color: "#295fab",
            filterable: true,
        },
        [this.statusesTicket.REFUSED]: {
            label: this.scheduledStatusesTicket.RIFIUTATO,
            status: this.statusesTicket.REFUSED,
            color: "#f2123e",
            filterable: true,
        },
        [this.statusesTicket.ONLINE]: {
            label: this.scheduledStatusesTicket.ONLINE,
            status: this.statusesTicket.ONLINE,
            color: "#077d17",
            filterable: true,
        },
    };
    public filterStatuses = Object.values(this.filterStatusMap).filter(
        (i) => i.filterable
    );
    public showCalendar: boolean = false;

    @ViewChild(MatPaginator) paginator: MatPaginator;
    constructor(
        private apiTicketService: ApiTicketService,
        public dialog: MatDialog,
        private route: ActivatedRoute,
        private router: Router,
        private toast: NotificationsService,
        private spinner: NgxSpinnerService
    ) {
        this.range = new FormGroup({
            start: new FormControl<Date | null>(null),
            end: new FormControl<Date | null>(null),
        });
    }

    ngOnInit() {
        if (!this.route.snapshot.paramMap.get("viewAll")) {
            this.showCalendar = false;
            this.size = 5;
            this.pageNumber = 0;
            this.nElemForPage = [5, 10, 15];
        } else {
            this.showCalendar = true;
            this.size = 50;
            this.pageNumber = 0;
            this.nElemForPage = [50, 100, 150];
        }

        this.id_user = Number(this.route.snapshot.paramMap.get("id_user"));
        this.dataSource.paginator = this.paginator;
        this.getData();
    }

    canDeleteTicket(ticket: ITicket) {
        if (!ticket) {
            return false;
        }
        var date = moment
            .tz(new Date(), "Europe/Rome")
            .add(environment.timeHoursBeforeDeletion, "hour");
        return date.isBefore(ticket?.calendarEvent?.time_start);
    }

    onDeleteEvent(ticketSel: ITicket) {
        if (!this.canDeleteTicket(ticketSel)) {
            return;
        }

        this.dialog
            .open(DialogConfirm, {
                data: {
                    msg: "Sei sicuro di voler cancellare questo appuntamento?",
                },
            })
            .afterClosed()
            .pipe(
                filter((data) => !!data),
                mergeMap(() => {
                    const ticket: ITicket = assign(
                        {},
                        omit(ticketSel, "schedule_start"),
                        {
                            id_status: TicketStatuses.DELETED,
                        }
                    );
                    return this.apiTicketService.update(ticket);
                })
            )
            .subscribe({
                next: (data) => {
                    this.getData();
                    this.toast.success(
                        "Cancellazione Appuntamento",
                        "Appuntamento cancellato con successo"
                    );
                },
                error: (err) => {
                    this.toast.error(
                        "Cancellazione Appuntamento",
                        "Errore nella cancellazione dell'appuntamento"
                    );
                },
            });
    }

    openDetail(ticket: ITicket) {
        this.dialog
            .open(DetailComponent, {
                hasBackdrop: true,
                data: {
                    ITicket: ticket,
                    canDeleteTicket: this.canDeleteTicket(ticket),
                    statusDetails: this.filterStatusMap[ticket.id_status],
                },
            })
            .afterClosed()
            .pipe(filter(Boolean))
            .subscribe(
                (returnValue: {
                    cta: "goToVideochat" | "delete";
                    ticket: any;
                }) => {
                    if (returnValue.cta === "goToVideochat") {
                        this.goToVideochat(returnValue.ticket);
                    } else if (returnValue.cta === "delete") {
                        this.onDeleteEvent(returnValue.ticket);
                    }
                }
            );
    }

    goToVideochat(ticket) {
        if (ticket.id_status === TicketStatuses.ONLINE) {
            this.router.navigate(["videochat", ticket.id]);
        }
    }

    getData() {
        if (this.id_user != 0) {
            this.spinner.show();
            this.spinnerOn = true;

            const today = new Date();
            const start_date =
                new Date(today.setHours(0, 0, 0, 0))
                    .toISOString()
                    .split("T")[0] + " 00:00:00";
            const statusesToSearch: number[] = [
                TicketStatuses.SCHEDULED_UNASSIGNED,
                TicketStatuses.SCHEDULED_ASSIGNED,
                TicketStatuses.ONLINE,
                TicketStatuses.REFUSED,
            ];

            let obj: TicketScheduleRequest = {
                id_user: this.id_user,
                schedule_start: start_date,
                platform: environment.platform,
                id_statuses: statusesToSearch,
            };

            this.apiTicketService
                .getTicketScheduledListUsingPost(obj)
                .subscribe((elements) => {
                    this.dataSource = new MatTableDataSource<ITicketScheduled>(
                        elements?.map((ticket) => {
                            return Object.assign(ticket, {
                                schedule_start: moment.tz(
                                    ticket?.date_time
                                        ?.toString()
                                        .replace("Z", ""),
                                    "Europe/Rome"
                                ),
                            });
                        })
                    );

                    const orderedData = orderBy(
                        elements,
                        ["calendarEvent.time_start"],
                        ["asc"]
                    );
                    this.dataSource = new MatTableDataSource<ITicketScheduled>(
                        orderedData
                    );
                    this.dataSource.paginator = this.paginator;
                    this.length = orderedData.length;

                    this.spinner.hide();
                    this.spinnerOn = false;
                });
        }

        this.spinner.hide();
        this.spinnerOn = false;
    }

    pageEvents(event: any) {
        this.size = event.pageSize;
        this.pageNumber = event.pageIndex;
        this.getData();
    }

    onButtonFilterClick(status: number) {
        this.dataSource.filterPredicate = (
            data: ITicketScheduled,
            filter: string
        ) => {
            return data.id_status === +filter;
        };
        this.dataSource.filter = status.toString();
    }
}
